import type { Theme } from '@emotion/react'
import { useEffect, useState } from 'react'

const VolumeBar = ({
  active = false,
  micMuted = false,
}: {
  active?: boolean
  micMuted?: boolean
}) => {
  const getBackgroundColor = (theme: Theme) => {
    if (micMuted) {
      return theme.semantic.ColorContentDisabled
    }
    if (active) {
      return theme.semantic.ColorSuccessDefault
    }
    return theme.semantic.ColorAccessoryReduced
  }
  return (
    <div
      css={theme => ({
        height: 3,
        borderRadius: 2,
        backgroundColor: getBackgroundColor(theme),
        margin: '1px 0',
        width: 10,
      })}
    />
  )
}

const SAMPLE_INTERVAL = 50

export const MicrophoneLevelWidget = ({
  micMuted = false,
}: {
  micMuted: boolean
}) => {
  let volumeCallback: VoidFunction | null = null
  let volumeInterval: ReturnType<typeof setInterval> | null = null

  const [micInputLevel, setMicInputLevel] = useState(0)

  const getMicLevel = async () => {
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
        },
      })

      const audioContext = new AudioContext()
      const audioSource = audioContext.createMediaStreamSource(audioStream)

      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 512
      analyser.minDecibels = -127
      analyser.maxDecibels = 0
      analyser.smoothingTimeConstant = 0.4
      audioSource.connect(analyser)

      const volumes = new Uint8Array(analyser.frequencyBinCount)

      volumeCallback = () => {
        analyser.getByteFrequencyData(volumes)
        let volumeSum = 0
        for (const volume of volumes) volumeSum += volume
        const averageVolume = volumeSum / volumes.length

        const normalized = Math.max(averageVolume / 127, 0.001)
        const db = 20 * Math.log10(normalized)

        const dbMin = -40
        const dbMax = -5
        const volumePercentage = Math.max(
          0,
          Math.min(100, ((db - dbMin) / (dbMax - dbMin)) * 100),
        )

        setMicInputLevel(volumePercentage)
      }
    } catch (e) {
      console.error('Failed to initialize volume visualizer...', e)
    }

    if (volumeCallback !== null) {
      volumeInterval = setInterval(volumeCallback, SAMPLE_INTERVAL)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: Can't memoize the dynamically allocated callback function
  useEffect(() => {
    if (!micMuted) {
      getMicLevel()
    }

    return () => {
      if (volumeInterval !== null) {
        clearInterval(volumeInterval)
      }
    }
  }, [micMuted, volumeInterval])

  return (
    <div css={{ display: 'flex', flexDirection: 'column', padding: '5px' }}>
      {[6, 5, 4, 3, 2, 1].map(index => (
        <VolumeBar
          micMuted={micMuted}
          active={!micMuted && micInputLevel >= (100 / 6) * index}
          key={index}
        />
      ))}
    </div>
  )
}
